<template>
	<div class="dt">
		<p v-if="!signals" class="dt-note">{{ loading ? "Loading…" : "Nothing to show yet." }}</p>
		<template v-else-if="signals.testMode">
			<p class="dt-intro">
				Test mode: scores fade in seconds so you can watch them. Each bar was full at your last activity
				and drains as the score fades. Do something on the site and it fills up again.
			</p>
			<section v-for="area in liveAreas" :key="area.key" class="dt-card" :style="{ '--area': area.color }">
				<header class="dt-head">
					<h3>{{ area.title }}</h3>
					<span class="dt-half">halves every {{ durationLabel(area.half) }}</span>
				</header>
				<p class="dt-what">{{ area.what }}</p>
				<p v-if="!area.items.length" class="dt-note">{{ area.empty }}</p>
				<div v-for="item in area.items" :key="item.key" class="dt-item" :class="{ 'dt-item--sub': item.sub }">
					<div class="dt-row">
						<span class="dt-name">{{ item.label }}</span>
						<span class="dt-values"><b>{{ Math.round(item.left) }}%</b> of score left</span>
					</div>
					<div
						class="dt-bar"
						role="progressbar"
						:aria-label="item.label"
						aria-valuemin="0"
						aria-valuemax="100"
						:aria-valuenow="Math.round(item.left)"
					>
						<span class="dt-bar-live" :class="{ 'dt-bar-live--low': item.left < 25 }" :style="{ width: `${item.left.toFixed(1)}%` }" />
					</div>
				</div>
			</section>
		</template>
		<template v-else>
			<p class="dt-intro">If you stopped using the site for…</p>
			<nav class="dt-horizons" aria-label="Time away">
				<button
					v-for="seconds in horizons"
					:key="seconds"
					type="button"
					class="dt-horizon"
					:class="{ 'dt-horizon--active': seconds === horizon }"
					:aria-pressed="seconds === horizon"
					@click="picked = seconds"
				>{{ durationLabel(seconds) }}</button>
			</nav>
			<p class="dt-legend">
				<span class="dt-key dt-key--kept" /> stays
				<span class="dt-key dt-key--lost" /> fades in {{ durationLabel(horizon) }}
			</p>

			<section v-for="area in areas" :key="area.key" class="dt-card" :style="{ '--area': area.color }">
				<h3>{{ area.title }}</h3>
				<p class="dt-what">{{ area.what }}</p>
				<p v-if="!area.items.length" class="dt-note">{{ area.empty }}</p>
				<div v-for="item in area.items" :key="item.key" class="dt-item" :class="{ 'dt-item--sub': item.sub }">
					<div class="dt-row">
						<span class="dt-name">{{ item.label }}</span>
						<span class="dt-values">
							{{ fmt(item.now) }} → <b>{{ fmt(later(item)) }}</b>
							<span v-if="lost(item)" class="dt-lost">−{{ fmt(lost(item)) }}</span>
						</span>
					</div>
					<div
						class="dt-bar"
						role="img"
						:aria-label="`${item.label}: ${fmt(item.now)} now, ${fmt(later(item))} after ${durationLabel(horizon)}`"
					>
						<span class="dt-bar-now" :style="{ width: share(item.now, area.max) }">
							<span class="dt-bar-kept" :style="{ width: share(later(item), item.now) }" />
						</span>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
import { AREA_COLORS, durationLabel, tagName } from "./hudFormat";

export default {
	name: "HudDecayTab",
	props: {
		signals: { type: Object, default: null },
		// When this response arrived (ms), so each live value lines up with the second it belongs to.
		receivedAt: { type: Number, default: null },
		loading: { type: Boolean, default: false },
	},
	data() {
		return { picked: null, clock: Date.now(), ticker: null };
	},
	computed: {
		// Seconds; an older response states hours only.
		horizons() {
			const s = this.signals || {};
			return s.horizonSeconds || (s.horizonHours || []).map((hours) => hours * 3600);
		},
		// Defaults to the longest horizon, where the fading is easiest to see.
		horizon() {
			return this.horizons.includes(this.picked) ? this.picked : this.horizons[this.horizons.length - 1];
		},
		// Test mode only: which of the next seconds' values to show, 0 being the value as read.
		liveStep() {
			const live = this.signals && this.signals.live;
			if (!live || !(this.receivedAt > 0)) return 0;
			const step = Math.floor((this.clock - this.receivedAt) / (live.stepSeconds * 1000));
			return Math.max(0, Math.min(live.steps, step));
		},
		areas() {
			const s = this.signals;
			const half = s.halfLifeSeconds || secondsFromDays(s.halfLifeDays);
			const career = [];
			s.career.forEach((domain) => {
				if (!domain.score) return;
				career.push({ key: domain.tag, label: domain.name || tagName(domain.tag), now: this.liveValue(domain.score, domain.scoreLive), projected: domain.scoreProjected, peak: domain.scorePeak });
				domain.subdomains.forEach((sub) => {
					if (sub.score) career.push({ key: `${domain.tag}/${sub.tag}`, label: sub.name || tagName(sub.tag), now: this.liveValue(sub.score, sub.scoreLive), projected: sub.scoreProjected, peak: sub.scorePeak, sub: true });
				});
			});
			const series = (key, label, entry, sub) => ({ key, label, now: this.liveValue(entry.value, entry.live), projected: entry.projected, peak: entry.peak, sub });
			// Raw per-area values for one signal (views or recent), domains with their subdomains.
			const perArea = (field) => {
				const rows = [];
				s.career.forEach((domain) => {
					if (domain[field].value > 0) rows.push(series(`${field}:${domain.tag}`, domain.name || tagName(domain.tag), domain[field]));
					domain.subdomains.forEach((sub) => {
						if (sub[field].value > 0) rows.push(series(`${field}:${domain.tag}/${sub.tag}`, sub.name || tagName(sub.tag), sub[field], true));
					});
				});
				return rows;
			};
			const momentum = s.learningMomentum;
			const areas = [
				{
					key: "career",
					title: "Career direction",
					color: AREA_COLORS.career,
					half: half.career,
					what: `Your 0–100 career scores. Lose half every ${durationLabel(half.career)}.`,
					empty: "No career scores yet.",
					items: career,
				},
				{
					key: "recent",
					title: "Recent focus",
					color: AREA_COLORS.recent,
					half: half.recent,
					what: `What you looked at lately. Loses half every ${durationLabel(half.recent)}.`,
					empty: "No recent focus yet.",
					items: perArea("recent"),
				},
				{
					key: "views",
					title: "Views",
					color: AREA_COLORS.activity,
					half: half.views,
					what: `Browsing that feeds career direction. Loses half every ${durationLabel(half.views)}.`,
					empty: "No views yet.",
					items: perArea("views"),
				},
				{
					key: "momentum",
					title: "Learning momentum",
					color: AREA_COLORS.momentum,
					half: half.momentum,
					what: `Loses half every ${durationLabel(half.momentum)}.`,
					empty: "No learning activity yet.",
					items: momentum.overall.value > 0
						? [series("overall", "Overall", momentum.overall), ...momentum.byDomain.map((d) => series(d.tag, d.name || tagName(d.tag), d, true))]
						: [],
				},
				{
					key: "sections",
					title: "Dashboard sections",
					color: AREA_COLORS.sections,
					half: half.section,
					what: `Sets the dashboard order. Fades fastest: loses half every ${durationLabel(half.section)}.`,
					empty: "No section interest yet.",
					items: s.sections.map((row) => series(row.name, tagName(row.name), row)),
				},
			];
			// Bars in one card share a scale, so a bigger score draws a longer bar.
			return areas.map((area) => ({ ...area, max: Math.max(0, ...area.items.map((item) => item.now || 0)) }));
		},
		// Test mode: each row as the share left of its value at the last activity.
		liveAreas() {
			return this.areas.map((area) => ({
				...area,
				what: LIVE_WHAT[area.key],
				items: area.items
					.filter((item) => item.now > 0 || item.peak > 0)
					.map((item) => {
						const peak = item.peak > 0 ? item.peak : area.max;
						return { ...item, left: peak > 0 ? Math.max(0, Math.min(100, ((item.now || 0) / peak) * 100)) : 0 };
					}),
			}));
		},
	},
	watch: {
		"signals.testMode": {
			immediate: true,
			handler(on) {
				if (on) this.startTicking();
				else this.stopTicking();
			},
		},
	},
	beforeDestroy() {
		this.stopTicking();
	},
	methods: {
		durationLabel,
		startTicking() {
			if (this.ticker) return;
			this.clock = Date.now();
			this.ticker = setInterval(() => {
				this.clock = Date.now();
			}, 1000);
		},
		stopTicking() {
			if (this.ticker) clearInterval(this.ticker);
			this.ticker = null;
		},
		// The value for the current second; past the last one it holds until the next read.
		liveValue(value, live) {
			if (!this.liveStep || !Array.isArray(live) || !live.length) return value;
			const next = live[Math.min(this.liveStep, live.length) - 1];
			return typeof next === "number" && isFinite(next) ? next : value;
		},
		// Small values keep one decimal so a fading 0.8 does not read as 1.
		fmt(value) {
			if (!(typeof value === "number" && isFinite(value))) return "0";
			return Math.abs(value) < 10 ? value.toFixed(1).replace(/\.0$/, "") : String(Math.round(value));
		},
		later(item) {
			const point = (item.projected || []).find((p) => (p.seconds != null ? p.seconds : p.hours * 3600) === this.horizon);
			return point && typeof point.value === "number" && isFinite(point.value) ? point.value : item.now;
		},
		lost(item) {
			const drop = (item.now || 0) - this.later(item);
			return drop >= 0.05 ? drop : 0;
		},
		share(value, of) {
			if (!(of > 0) || !(value > 0)) return "0%";
			return `${Math.min(100, (value / of) * 100).toFixed(1)}%`;
		},
	},
};

// Test mode card blurbs: what each score is, since the half-life sits in the card header.
const LIVE_WHAT = {
	career: "Your 0–100 score per field, from what you apply to, buy and view.",
	recent: "What you looked at lately. Fades faster than career direction.",
	views: "Views that feed career direction once a field has 3 or more.",
	momentum: "How actively you are learning right now.",
	sections: "Decides whether Jobs or Courses comes first on the home page.",
};

// An older response states half-lives in days only.
function secondsFromDays(days) {
	const out = {};
	Object.keys(days || {}).forEach((name) => {
		out[name] = days[name] * 86400;
	});
	return out;
}
</script>

<style scoped>
.dt {
	padding: 12px 14px 24px;
}
.dt-intro {
	margin: 0 0 8px;
	font-size: 13px;
	line-height: 1.5;
	color: #464667;
}
.dt-note {
	margin: 2px 0 0;
	font-size: 12.5px;
	color: #72738e;
}
.dt-horizons {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 8px;
}
.dt-horizon {
	padding: 5px 10px;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	background: #fff;
	color: #464667;
	font: 600 11.5px "Montserrat", sans-serif;
	cursor: pointer;
}
.dt-horizon:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}
.dt-horizon--active {
	border-color: #20639b;
	background: #20639b;
	color: #fff;
}
.dt-legend {
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 0 0 12px;
	font-size: 12px;
	color: #72738e;
}
.dt-key {
	display: inline-block;
	width: 14px;
	height: 8px;
	border-radius: 4px;
}
.dt-key--kept {
	background: #20639b;
}
.dt-key--lost {
	margin-left: 6px;
	background: #c9d8e6;
}

.dt-card {
	padding: 12px 14px 10px;
	margin-bottom: 10px;
	border: 1px solid #e2e8f0;
	border-left: 4px solid var(--area);
	border-radius: 10px;
}
.dt-card h3 {
	margin: 0;
	font: 700 14px "Montserrat", sans-serif;
	color: var(--area);
}
.dt-what {
	margin: 2px 0 8px;
	font-size: 12px;
	color: #72738e;
}
.dt-item {
	padding: 5px 0;
}
.dt-item--sub {
	padding-left: 12px;
}
.dt-row {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 4px;
}
.dt-name {
	min-width: 0;
	overflow-wrap: anywhere;
	font: 700 12.5px "Montserrat", sans-serif;
	color: #1a4164;
}
.dt-item--sub .dt-name {
	font: 400 12.5px "Lato", sans-serif;
	color: #464667;
}
.dt-values {
	flex: none;
	font: 500 12px "Montserrat", sans-serif;
	color: #72738e;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}
.dt-values b {
	font-weight: 700;
	color: #1a4164;
}
.dt-lost {
	margin-left: 4px;
	color: #b3261e;
}
.dt-bar {
	height: 8px;
	border-radius: 4px;
	background: #f1f4f8;
	overflow: hidden;
}
.dt-bar-now {
	display: block;
	height: 100%;
	border-radius: 4px;
	background: color-mix(in srgb, var(--area) 25%, #fff);
}
.dt-bar-kept {
	display: block;
	height: 100%;
	border-radius: 4px;
	background: var(--area);
	transition: width 0.2s ease;
}
.dt-head {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	justify-content: space-between;
	gap: 2px 8px;
}
.dt-half {
	font: 600 11px "Montserrat", sans-serif;
	color: #72738e;
	white-space: nowrap;
}
.dt-bar-live {
	display: block;
	height: 100%;
	border-radius: 4px;
	background: var(--area);
	transition: width 1s linear;
}
.dt-bar-live--low {
	background: color-mix(in srgb, var(--area) 45%, #fff);
}
@media (prefers-reduced-motion: reduce) {
	.dt-bar-kept,
	.dt-bar-live {
		transition: none;
	}
}
</style>
